import React, { useState, useEffect } from 'react';
import './InstallPrompt.css';

interface InstallPromptProps {
  appName: string;
  themeColor: string;
}

// Get subdomain-specific icon
const getSubdomainIcon = () => {
  const config = (window as any).__SUBDOMAIN_CONFIG__ || {};
  const host = window.location.hostname;

  if (config.isWholesale || host.startsWith('wholesale.')) {
    return '/wholesale-icon-192.png';
  } else if (config.isBridgeClub || host.startsWith('bridgeclub.')) {
    return '/bridgeclub-icon-192.png';
  } else if (config.isStoreClub || host.startsWith('storeclub.')) {
    return '/storeclub-icon-192.png';
  }
  return '/logo192.png';
};

const InstallPrompt: React.FC<InstallPromptProps> = ({ appName, themeColor }) => {
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [iconSrc] = useState(getSubdomainIcon);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently (don't show for 7 days)
    const dismissed = localStorage.getItem('installPromptDismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSince = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return;
    }

    // Listen for install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // iOS detection - show manual instructions
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && !window.matchMedia('(display-mode: standalone)').matches) {
      setShowPrompt(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const result = await installPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShowPrompt(false);
        setIsInstalled(true);
      }
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', new Date().toISOString());
  };

  if (!showPrompt || isInstalled) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div className="install-prompt" style={{ backgroundColor: themeColor }}>
      <div className="install-prompt-content">
        <img
          src={iconSrc}
          alt={appName}
          className="install-prompt-icon"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            objectFit: 'contain',
          }}
        />
        <div className="install-prompt-text">
          <strong>Get the {appName} App</strong>
          {isIOS ? (
            <span>Tap <strong>Share</strong> then <strong>"Add to Home Screen"</strong></span>
          ) : (
            <span>Install for quick access</span>
          )}
        </div>
      </div>
      <div className="install-prompt-actions">
        {!isIOS && (
          <button className="install-btn" onClick={handleInstall}>
            Install
          </button>
        )}
        <button className="dismiss-btn" onClick={handleDismiss}>
          ✕
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
